import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useConsent } from '@/contexts/ConsentContext';
import { Shield, BarChart3, Megaphone } from 'lucide-react';

const FONT_H = { fontFamily: "'Outfit', sans-serif" };

const CATEGORIES = [
  {
    key: 'necessary',
    icon: Shield,
    title: 'Strictly necessary',
    always: true,
    desc:
      'Required for the site to function - these keep you signed in, secure your session, and let you complete a payment. Cannot be disabled.',
    examples: 'admin_token, player_token, XSRF / session cookies, Stripe & Revolut payment cookies',
  },
  {
    key: 'analytics',
    icon: BarChart3,
    title: 'Analytics',
    always: false,
    desc:
      'Help us understand which features get used so we can improve the product. Anonymised, aggregated usage data only.',
    examples: 'Currently none - reserved for future privacy-friendly analytics (e.g. PostHog EU) if added.',
  },
  {
    key: 'marketing',
    icon: Megaphone,
    title: 'Marketing',
    always: false,
    desc:
      'Used for personalised content or re-targeting across other sites. SportSweeps does not currently run any marketing cookies.',
    examples: 'None.',
  },
];

export function CookiePreferencesModal() {
  const { state, hasDecided, preferencesOpen, closePreferences, savePreferences, acceptAll, rejectAll } = useConsent();
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (preferencesOpen) {
      setAnalytics(hasDecided ? Boolean(state.analytics) : false);
      setMarketing(hasDecided ? Boolean(state.marketing) : false);
    }
  }, [preferencesOpen, hasDecided, state.analytics, state.marketing]);

  const getValue = (key) => {
    if (key === 'necessary') return true;
    if (key === 'analytics') return analytics;
    if (key === 'marketing') return marketing;
    return false;
  };

  const setValue = (key, v) => {
    if (key === 'analytics') setAnalytics(v);
    if (key === 'marketing') setMarketing(v);
  };

  return (
    <Dialog open={preferencesOpen} onOpenChange={(o) => !o && closePreferences()}>
      <DialogContent className="max-w-lg" data-testid="cookie-preferences-modal">
        <DialogHeader>
          <DialogTitle style={FONT_H} className="text-xl">Cookie preferences</DialogTitle>
          <DialogDescription>
            Choose which categories of cookies you&apos;re happy with. You can change this at any time via the footer link.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[55vh] overflow-y-auto py-1">
          {CATEGORIES.map(({ key, icon: Icon, title, always, desc, examples }) => (
            <div key={key} className="rounded-xl border border-gray-200 p-4" data-testid={`cookie-cat-${key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900" style={FONT_H}>{title}</div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">{desc}</p>
                    <p className="text-[11px] text-gray-400 mt-1.5"><span className="font-medium text-gray-500">Examples: </span>{examples}</p>
                  </div>
                </div>
                {always ? (
                  <span className="text-[10px] uppercase tracking-wide font-bold text-gray-400 px-2 py-1 rounded-full bg-gray-100 flex-shrink-0">
                    Always on
                  </span>
                ) : (
                  <Switch
                    checked={getValue(key)}
                    onCheckedChange={(v) => setValue(key, v)}
                    data-testid={`cookie-toggle-${key}`}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={rejectAll} data-testid="cookie-modal-reject">
            Reject all
          </Button>
          <Button variant="outline" size="sm" onClick={acceptAll} data-testid="cookie-modal-accept-all">
            Accept all
          </Button>
          <Button
            size="sm"
            onClick={() => savePreferences({ analytics, marketing })}
            className="text-white"
            style={{ background: 'linear-gradient(135deg, #E91E8C 0%, #6C3CE0 100%)' }}
            data-testid="cookie-modal-save"
          >
            Save preferences
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
